import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { useSeo } from "@/hooks/useSeo";
import { site, testimonials } from "@/mocks/content";
import TipForm from "./components/TipForm";

const principles = [
  {
    icon: "ri-file-search-line",
    title: "只整理公开信息",
    desc: "我们只使用公开发布的动态、声明与流传证据，不采用来源不明的私密材料。",
  },
  {
    icon: "ri-question-mark",
    title: "标注待确认部分",
    desc: "尚未获得当事方确认的内容会明确标注，避免把推测写成结论。",
  },
  {
    icon: "ri-shield-user-line",
    title: "不碰隐私信息",
    desc: "不发布私人联系方式、身份信息与未经授权的私人影像，收到提醒会立即复核。",
  },
  {
    icon: "ri-refresh-line",
    title: "支持更正与更新",
    desc: "事件有反转会更新原文并注明修改记录，当事人可随时提交更正依据。",
  },
];

export default function AboutPage() {
  useSeo({
    title: "关于我们_内容原则与投稿方式 - 吃瓜日报",
    description:
      "了解吃瓜日报的定位与内容原则：只整理公开可查的网红事件，标注待确认内容，不发布隐私信息。页面同时提供爆料投稿入口与更正联系方式。",
    keywords: "关于吃瓜日报,内容原则,爆料投稿,网红吃瓜,今日瓜",
    canonical: "https://jcspower.cn/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于吃瓜日报",
        description: "吃瓜日报的定位、内容原则与联系方式",
        url: "https://jcspower.cn/about",
      },
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        name: "联系吃瓜日报",
        url: "https://jcspower.cn/about",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: site.name,
        url: "https://jcspower.cn/",
        email: site.email,
      },
    ],
  });

  return (
    <SiteLayout>
      <div className="w-full border-b border-background-200/70 bg-background-100/50">
        <div className="mx-auto w-full px-4 py-8 md:px-6 md:py-12">
          <Breadcrumbs items={[{ label: "关于我们" }]} />
          <h1 className="mt-4 font-heading text-2xl font-black text-foreground-950 md:text-4xl">
            关于吃瓜日报
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            {site.name}成立于 {site.since} 年，是一个专注网红圈公开事件整理的内容站点。
            我们不生产传闻，只把已经摆在台面上的信息，按时间顺序理清楚，让你自己看清整件事。
          </p>
        </div>
      </div>

      <section aria-labelledby="position-title" className="w-full bg-background-50">
        <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
            <div>
              <h2 id="position-title" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                我们是谁，为谁而做
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-600">
                网红内容更新快、信息碎、反转多。一条十几秒的切片就足以点燃舆论，
                而大多数围观者其实并不清楚事情的前因后果。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                吃瓜日报希望改变这一点：我们把散落在各平台的公开信息聚合起来，
                整理成清晰的事件时间线与证据清单，让每个读者都能在五分钟内建立起自己的判断。
              </p>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                我们的读者包括关注泛娱乐内容的普通用户、内容行业从业者，
                以及需要快速了解舆情脉络的品牌与商务人员。
              </p>
            </div>
            <div className="h-64 w-full overflow-hidden rounded-lg lg:h-auto">
              <img
                src="https://readdy.ai/api/search-image?query=stylized%20abstract%20editorial%20illustration%20of%20a%20long%20paper%20timeline%20with%20colored%20markers%20and%20magnifying%20glass%2C%20coral%20red%20sage%20green%20and%20cream%20palette%2C%20paper%20collage%20texture%2C%20warm%20minimal%20background&width=1200&height=900&seq=cg-about-main&orientation=landscape"
                alt="吃瓜日报内容整理流程示意插画"
                title="吃瓜日报内容整理流程 - 网红吃瓜今日瓜"
                className="h-full w-full object-cover object-top"
              />
            </div>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-3 md:grid-cols-4">
            {site.stats.map((stat) => (
              <div
                key={stat.label}
                className="rounded-lg border border-background-200/80 bg-background-100/60 p-4 text-center"
              >
                <p className="font-heading text-xl font-bold text-primary-600 md:text-2xl">{stat.value}</p>
                <p className="mt-1 text-xs text-foreground-500">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section aria-labelledby="principle-title" className="w-full bg-background-100/60">
        <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <h2 id="principle-title" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            四条内容原则
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            这些原则决定了什么内容会被我们写出来，什么内容会被我们放弃
          </p>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            {principles.map((item) => (
              <article
                key={item.title}
                className="flex gap-4 rounded-lg border border-background-200/80 bg-background-50 p-5"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                  <i className={`${item.icon} text-xl`} aria-hidden="true" />
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                  <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section aria-labelledby="voice-title" className="w-full bg-background-50">
        <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <h2 id="voice-title" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            围观群众怎么说
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {testimonials.map((item) => (
              <blockquote
                key={item.name}
                className="flex flex-col rounded-lg border border-background-200/80 bg-background-100/50 p-5"
              >
                <span className="flex h-5 w-5 items-center justify-center text-secondary-500">
                  <i className="ri-double-quotes-l text-base" aria-hidden="true" />
                </span>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">{item.text}</p>
                <footer className="mt-4 flex items-center gap-2 border-t border-background-200/70 pt-3 text-xs">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-secondary-200 font-heading text-[11px] font-bold text-secondary-900">
                    {item.name.slice(0, 1)}
                  </span>
                  <span className="font-medium text-foreground-800">{item.name}</span>
                  <span className="text-foreground-500">· {item.role}</span>
                </footer>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      <section id="tips" aria-labelledby="tips-title" className="w-full bg-background-100/60 scroll-mt-28">
        <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h2 id="tips-title" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                有瓜想说？欢迎投稿
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                如果你手上有公开可查的线索，欢迎提交给我们。我们更希望收到带出处的链接或截图，
                这样核实起来会快很多。投稿可以匿名，邮箱只用于信息核实。
              </p>
              <ul className="mt-5 flex flex-col gap-3">
                {[
                  "公开动态链接、视频地址优先",
                  "截图请尽量保留发布时间与来源",
                  "不接受来源不明的私密材料",
                  "涉及个人隐私的内容不予采用",
                ].map((tip) => (
                  <li key={tip} className="flex items-start gap-2.5 text-sm text-foreground-700">
                    <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-accent-600">
                      <i className="ri-check-line text-base" aria-hidden="true" />
                    </span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-7">
              <TipForm />
            </div>
          </div>
        </div>
      </section>

      <section aria-labelledby="contact-title" className="w-full bg-background-50">
        <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
          <h2 id="contact-title" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            联系我们
          </h2>
          <p className="mt-2 text-sm text-foreground-600">
            内容更正、侵权投诉、商务合作，都可以通过下面的邮箱联系我们
          </p>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="rounded-lg border border-background-200/80 bg-background-100/60 p-5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className="ri-mail-line text-lg" aria-hidden="true" />
              </span>
              <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">内容更正与投诉</h3>
              <p className="mt-1.5 text-sm text-foreground-600">{site.email}</p>
            </div>
            <div className="rounded-lg border border-background-200/80 bg-background-100/60 p-5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                <i className="ri-lightbulb-line text-lg" aria-hidden="true" />
              </span>
              <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">线索投稿</h3>
              <p className="mt-1.5 text-sm text-foreground-600">{site.tipEmail}</p>
            </div>
            <div className="rounded-lg border border-background-200/80 bg-background-100/60 p-5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-secondary-100 text-secondary-800">
                <i className="ri-time-line text-lg" aria-hidden="true" />
              </span>
              <h3 className="mt-3 font-heading text-sm font-bold text-foreground-950">响应时间</h3>
              <p className="mt-1.5 text-sm text-foreground-600">工作日 10:00 - 19:00，一般 24 小时内回复</p>
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-background-200/80 bg-background-100/60 p-5">
            <p className="text-sm leading-relaxed text-foreground-600">
              想继续围观？回到
              <Link to="/news" className="mx-1 font-medium text-primary-600 transition-colors duration-200 hover:text-primary-700">
                今日瓜列表
              </Link>
              看看最新的爆料，或者浏览
              <Link to="/topic/love" className="mx-1 font-medium text-primary-600 transition-colors duration-200 hover:text-primary-700">
                热门专题
              </Link>
              按分类慢慢看。
            </p>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}